import { Controller } from "@hotwired/stimulus"

// Choosing a reference by LOOKING at it.
//
// A surface is a material and a plate is a shape. "Polished grey marble with
// distinct dark veining" in a dropdown is a sentence ABOUT a picture, and the
// picture is what the renderer actually receives — so the library shows the
// pictures and this wires a card back to the field the form submits.
//
// Nothing is saved here. A card sets the hidden input and updates the trigger
// so the page tells the truth about what is selected; the choice is committed
// when Save is pressed. That keeps one Save for all five elements rather than
// five round trips, and lets a mistake be undone by simply not saving.
export default class extends Controller {
  connect() {
    this.onClick = this.onClick.bind(this)
    this.element.addEventListener("click", this.onClick)

    // Each library is fetched the first time it is opened, not on page load.
    // All five at once was ~350 cards and near half a megabyte of HTML, on a
    // page where most visits open one library or none.
    this.onShow = this.onShow.bind(this)
    this.element.querySelectorAll(".modal").forEach((modal) => {
      modal.addEventListener("show.bs.modal", this.onShow)
    })
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
    this.element.querySelectorAll(".modal").forEach((modal) => {
      modal.removeEventListener("show.bs.modal", this.onShow)
    })
  }

  async onShow(event) {
    const slot = event.target.querySelector(".manual-library-slot")
    if (!slot || slot.dataset.loaded === "true") return

    slot.dataset.loaded = "true"
    try {
      const response = await fetch(slot.dataset.libraryUrl, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) throw new Error(response.status)
      slot.outerHTML = await response.text()
    } catch (error) {
      // Say so rather than spinning forever: a library that will not load is
      // a thing to retry, not a thing to wait on.
      slot.dataset.loaded = "false"
      slot.innerHTML = "Could not load this library. Close and open it again."
    }
  }

  onClick(event) {
    const card = event.target.closest("[data-casting-role]")
    if (!card) return

    const role  = card.dataset.castingRole
    const value = card.dataset.castingValue || ""
    const label = card.dataset.castingLabel || ""
    const thumb = card.dataset.castingThumb || ""

    const field = document.getElementById(`casting-value-${role}`)
    if (field) field.value = value

    const name = document.getElementById(`casting-name-${role}`)
    if (name) name.textContent = label

    // The trigger's own state, so "chosen" and "not used" stay true to the
    // field rather than to whatever the page was rendered with.
    const trigger = document.getElementById(`casting-trigger-${role}`)
    if (trigger) {
      trigger.classList.toggle("manual-pick--chosen", value !== "" && value !== "none")
      trigger.classList.toggle("manual-pick--off", value === "none")

      const slot = trigger.querySelector(".manual-pick__thumb")
      if (slot) {
        if (thumb) {
          const img = document.createElement("img")
          img.src = thumb
          img.alt = ""
          img.className = "manual-pick__thumb"
          slot.replaceWith(img)
        } else if (slot.tagName === "IMG") {
          const span = document.createElement("span")
          span.className = "manual-pick__thumb"
          span.textContent = value === "none" ? "none" : "auto"
          slot.replaceWith(span)
        } else {
          slot.textContent = value === "none" ? "none" : (value ? "words" : "auto")
        }
      }
    }

    // One card carries the choice inside its own modal.
    const body = card.closest(".modal-body")
    if (body) {
      body.querySelectorAll(".manual-card--chosen").forEach((c) => c.classList.remove("manual-card--chosen"))
      if (card.classList.contains("manual-card")) card.classList.add("manual-card--chosen")
    }
  }
}
